import { useEffect, useId, type ReactNode } from 'react';

export interface DrawerProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  actions?: ReactNode;
  width?: 'default' | 'wide';
  fullScreen?: boolean;
  ariaDescribedBy?: string;
}

const Drawer = ({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  actions,
  width = 'default',
  fullScreen = false,
  ariaDescribedBy
}: DrawerProps) => {
  const titleId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget === event.target) {
      onClose();
    }
  };

  const className = ['ui-drawer', open ? 'ui-drawer--open' : '', width === 'wide' ? 'ui-drawer--wide' : '', fullScreen ? 'ui-drawer--fullscreen' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className} role="presentation" aria-hidden={!open}>
      <div className="ui-drawer__overlay" onClick={handleOverlayClick} />
      <aside className="ui-drawer__panel" role="dialog" aria-modal={open} aria-labelledby={titleId} aria-describedby={ariaDescribedBy}>
        <header className="ui-drawer__header">
          <div className="ui-drawer__title-group">
            <h2 id={titleId} className="ui-drawer__title">
              {title}
            </h2>
            {subtitle && <p className="ui-drawer__subtitle">{subtitle}</p>}
          </div>
          <div className="ui-drawer__header-controls">
            {actions}
            <button type="button" className="ui-drawer__close" aria-label="Close panel" onClick={onClose}>
              ×
            </button>
          </div>
        </header>
        <div className="ui-drawer__body">{children}</div>
        {footer && <footer className="ui-drawer__footer">{footer}</footer>}
      </aside>
    </div>
  );
};

export default Drawer;
